import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

export type Politeness = 'polite' | 'assertive';

export interface Announcer {
  /** Pushes a message to the shared live region. Noncritical confirmations only (§13.4 Toast / live region). */
  announce: (message: string, politeness?: Politeness) => void;
}

const AnnounceContext = createContext<Announcer | null>(null);

/**
 * One pair of live regions for the whole app so screen readers get a single, de-duplicated announcement queue.
 * Mount once near the root; `useAnnounce()` works anywhere below (and degrades to a no-op above).
 */
export function LiveRegionProvider({ children }: { children: ReactNode }) {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');
  const seq = useRef(0);
  const announce = useCallback((message: string, politeness: Politeness = 'polite') => {
    // Alternate a zero-width suffix so identical consecutive messages are still re-announced.
    seq.current = (seq.current + 1) % 2;
    const text = seq.current ? message : `${message}${String.fromCharCode(0x200b)}`;
    if (politeness === 'assertive') setAssertive(text);
    else setPolite(text);
  }, []);
  const value = useMemo(() => ({ announce }), [announce]);
  return (
    <AnnounceContext.Provider value={value}>
      {children}
      <div className="wd-sr-only" aria-live="polite" aria-atomic="true" data-testid="wd-live-polite">
        {polite}
      </div>
      <div
        className="wd-sr-only"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        data-testid="wd-live-assertive"
      >
        {assertive}
      </div>
    </AnnounceContext.Provider>
  );
}

const noop: Announcer = { announce: () => {} };

export function useAnnounce(): Announcer {
  return useContext(AnnounceContext) ?? noop;
}
